import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Check, RotateCcw, SwitchCamera, Loader2 } from "lucide-react";
import { videoPoster } from "@/lib/imageCompress";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

const MAX_SECS = 120; // 2-minute cap
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

function pickMime() {
  const cands = ["video/mp4", "video/webm;codecs=vp8,opus", "video/webm;codecs=vp9,opus", "video/webm"];
  for (const c of cands) { try { if (window.MediaRecorder && MediaRecorder.isTypeSupported(c)) return c; } catch (e) {} }
  return "";
}

// In-app camera recorder: records at ~240p (low bitrate = small file), max 2 min with a
// live countdown, then shows a looping preview with Retake / Use video.
export default function VideoRecorder({ onClose, onUse }) {
  const { t } = useI18n();
  const fr = (typeof localStorage !== "undefined" && localStorage.getItem("rc_lang") === "fr");
  const liveRef = useRef(null);
  const previewRef = useRef(null);
  const streamRef = useRef(null);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const [phase, setPhase] = useState("idle"); // idle | recording | preview | using
  const [secs, setSecs] = useState(0);
  const [facing, setFacing] = useState("user");
  const [ready, setReady] = useState(false);
  const [recorded, setRecorded] = useState(null); // { url, blob, mime }

  const stopStream = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((tr) => tr.stop()); streamRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const startCamera = async (mode = facing) => {
    stopStream();
    setReady(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: { facingMode: mode, width: { ideal: 426 }, height: { ideal: 240 }, frameRate: { ideal: 24, max: 30 } },
      });
      streamRef.current = stream;
      if (liveRef.current) { liveRef.current.srcObject = stream; liveRef.current.muted = true; try { await liveRef.current.play(); } catch (e) {} }
      setReady(true);
    } catch (e) {
      toast.error(fr ? "Caméra/micro inaccessible. Autorisez l'accès." : "Couldn't access camera/mic. Allow access.");
      onClose();
    }
  };

  useEffect(() => {
    if (!navigator.mediaDevices || typeof MediaRecorder === "undefined") {
      toast.error(fr ? "Enregistrement vidéo non pris en charge ici" : "Video recording isn't supported here");
      onClose();
      return;
    }
    startCamera("user");
    return () => { stopStream(); if (recorded?.url) { try { URL.revokeObjectURL(recorded.url); } catch (e) {} } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flip = async () => {
    const next = facing === "user" ? "environment" : "user";
    setFacing(next);
    await startCamera(next);
  };

  const startRec = () => {
    if (!streamRef.current || phase === "recording") return;
    const mime = pickMime();
    let mr;
    try { mr = new MediaRecorder(streamRef.current, { ...(mime ? { mimeType: mime } : {}), videoBitsPerSecond: 500000 }); }
    catch (e) { try { mr = new MediaRecorder(streamRef.current); } catch (e2) { toast.error(fr ? "Enregistrement impossible" : "Recording failed"); return; } }
    chunksRef.current = [];
    mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
    mr.onstop = () => {
      const type = mr.mimeType || mime || "video/webm";
      const blob = new Blob(chunksRef.current, { type });
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      if (!blob.size) { setPhase("idle"); return; }
      const url = URL.createObjectURL(blob);
      setRecorded({ url, blob, mime: type });
      setPhase("preview");
    };
    mr.start();
    mrRef.current = mr;
    setPhase("recording");
    setSecs(0);
    timerRef.current = setInterval(() => setSecs((s) => {
      const n = s + 1;
      if (n >= MAX_SECS) { try { mr.stop(); } catch (e) {} }
      return n;
    }), 1000);
  };

  const stopRec = () => { if (phase === "recording") { try { mrRef.current && mrRef.current.stop(); } catch (e) {} } };

  const retake = () => {
    if (recorded?.url) { try { URL.revokeObjectURL(recorded.url); } catch (e) {} }
    setRecorded(null);
    setSecs(0);
    setPhase("idle");
    startCamera(facing);
  };

  const useVideo = async () => {
    if (!recorded) return;
    setPhase("using");
    const ext = recorded.mime.includes("mp4") ? "mp4" : "webm";
    const file = new File([recorded.blob], `recording-${Date.now()}.${ext}`, { type: recorded.mime });
    let poster = null;
    try { poster = await videoPoster(file); } catch (e) {}
    stopStream();
    onUse(file, poster);
  };

  const remaining = MAX_SECS - secs;

  return createPortal(
    <div className="fixed inset-0 z-[130] bg-black text-white flex flex-col" data-testid="video-recorder">
      <div className="flex items-center justify-between px-4 py-3 shrink-0 rc-safe-top">
        <button onClick={onClose} data-testid="recorder-close" className="h-9 w-9 grid place-items-center rounded-full bg-white/10"><X size={20} /></button>
        <span className="font-heading text-base flex items-center gap-2">
          {phase === "recording" && <span className="h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse" />}
          {phase === "recording" ? (fr ? "Enregistrement" : "Recording") : phase === "preview" ? (fr ? "Aperçu" : "Preview") : (fr ? "Vidéo" : "Record video")}
        </span>
        {phase === "idle" && ready ? (
          <button onClick={flip} data-testid="recorder-flip" className="h-9 w-9 grid place-items-center rounded-full bg-white/10"><SwitchCamera size={18} /></button>
        ) : <div className="w-9" />}
      </div>

      {/* Countdown */}
      {phase === "recording" && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-10 rounded-full bg-black/60 px-4 py-1.5 text-lg font-semibold tabular-nums" data-testid="recorder-countdown">
          {fmt(Math.max(0, remaining))}
        </div>
      )}

      <div className="flex-1 min-h-0 flex items-center justify-center overflow-hidden bg-black">
        {phase === "preview" ? (
          <video ref={previewRef} src={recorded?.url} data-testid="recorder-preview-video" loop autoPlay playsInline controls className="max-h-full max-w-full h-full w-full object-contain" />
        ) : (
          <video ref={liveRef} data-testid="recorder-live" muted playsInline autoPlay className="max-h-full max-w-full h-full w-full object-contain" style={{ transform: facing === "user" ? "scaleX(-1)" : "none" }} />
        )}
      </div>

      <div className="px-6 pb-8 pt-4 shrink-0 rc-safe-bottom">
        {phase === "preview" ? (
          <div className="flex items-center justify-center gap-3">
            <button onClick={retake} data-testid="recorder-retake" className="flex-1 max-w-[10rem] flex items-center justify-center gap-2 rounded-full bg-white/12 py-3 font-semibold">
              <RotateCcw size={18} /> {fr ? "Refaire" : "Retake"}
            </button>
            <button onClick={useVideo} data-testid="recorder-use" className="flex-1 max-w-[12rem] flex items-center justify-center gap-2 rounded-full bg-[var(--rc-gold)] text-black py-3 font-semibold">
              <Check size={18} /> {fr ? "Utiliser" : "Use video"}
            </button>
          </div>
        ) : phase === "using" ? (
          <div className="flex items-center justify-center gap-2 text-[var(--rc-gold)] py-3"><Loader2 className="animate-spin" size={20} /> {fr ? "Préparation…" : "Preparing…"}</div>
        ) : (
          <div className="flex items-center justify-center">
            {phase === "recording" ? (
              <button onClick={stopRec} data-testid="recorder-stop" className="grid place-items-center rounded-full border-4 border-white/80" style={{ height: 72, width: 72 }}>
                <span className="h-8 w-8 rounded-md bg-red-500" />
              </button>
            ) : (
              <button onClick={startRec} disabled={!ready} data-testid="recorder-start" className="grid place-items-center rounded-full border-4 border-white/80 disabled:opacity-40" style={{ height: 72, width: 72 }}>
                {ready ? <span className="h-14 w-14 rounded-full bg-red-500" /> : <Loader2 className="animate-spin" size={26} />}
              </button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
